import { useQuery } from '@fhs/client';
import { useAuthContext } from '@fhs-legacy/frontend/src/state/auth';
import { client } from '@fhs-legacy/frontend/src/state/graphql/client';

import { EXPIRY_DATE_PLACEHOLDER } from '../utils/constants';
import { GetGiftCardBalancePayload, GetGiftCardDataPayload } from '../utils/types';

import { GetGiftCardBalanceQuery } from './use-get-balance';
import { GetGiftCardDataQuery } from './use-get-gift-card-data';

type DigitalGiftCardBalanceResult = {
  currentBalance: number;
  cardNumber?: string;
  accountIdentifier?: string;
};

/**
 * Hook to fetch the real-time balance of the user's stored digital gift card.
 * This combines GetGiftCardData (to get card credentials) and GetGiftCardBalance (to get current balance).
 *
 * Use this instead of relying on userAccounts.giftCard.currentBalance which may be stale.
 */
export const useDigitalGiftCardBalanceQuery = () => {
  const { user } = useAuthContext();

  return useQuery({
    enabled: Boolean(user?.cognitoId),
    queryKey: ['digitalGiftCardBalance', user?.cognitoId],
    staleTime: 0,
    gcTime: 0,
    retry: false,
    queryFn: async (): Promise<DigitalGiftCardBalanceResult | null> => {
      // Step 1: Get the user's stored gift card credentials
      const { data: giftCardDataResponse } = await client.query({
        query: GetGiftCardDataQuery,
        fetchPolicy: 'network-only',
      });

      const giftCardData = giftCardDataResponse?.getGiftCardData as GetGiftCardDataPayload;

      if (!giftCardData?.giftCardData?.accountIdentifier) {
        return null;
      }

      // Step 2: Fetch the real-time balance using the account identifier
      const { data: balanceResponse } = await client.query({
        query: GetGiftCardBalanceQuery,
        fetchPolicy: 'no-cache',
        variables: {
          input: {
            token: giftCardData.giftCardData.accountIdentifier,
            pin: giftCardData.giftCardData.pin,
            expiryDate: EXPIRY_DATE_PLACEHOLDER,
            isPhysicalCardBalanceCheck: false,
          },
        },
      });

      const balance = balanceResponse?.getGiftCardBalance?.giftCard as GetGiftCardBalancePayload;

      return {
        currentBalance: balance?.currentBalance ?? 0,
        cardNumber: giftCardData.giftCardData.cardNumber,
        accountIdentifier: giftCardData.giftCardData.accountIdentifier,
      };
    },
  });
};
